import {useCostAvoidanceForRangeQuery, useScheduleQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {Skeleton} from '@mui/material'
import {
  getLocalizedDayMonthTimeFormat,
  usePlanningChartScheduleData,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, ProductionPlanningLSSettingsKeys} from '../../../consts'
import {useDebouncedValue, useLocalStorageSettings, usePeriodicAutofillCheck} from '../../../hooks'
import {CollapsibleCard} from '../../shared'

import {KPISectionTable} from './KPIsSectionTable'

type KPIsSectionSubtitleProps = {
  scheduleId: number
  from: Moment
  to: Moment
}

const KPIsSectionSubtitle: React.FC<KPIsSectionSubtitleProps> = ({scheduleId, from, to}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()
  const {plantId} = useUpmPlantContext()

  const {dataUpdatedAt} = useCostAvoidanceForRangeQuery({
    plantId,
    scheduleId,
    from: moment.utc(from).toISOString(),
    to: moment.utc(to).toISOString()
  })
  const tableUpdatedAt = moment.utc(dataUpdatedAt)

  return (
    <>
      {t('common.lastUpdate', {
        date: getLocalizedDayMonthTimeFormat(tableUpdatedAt, timezoneId, language)
      })}
    </>
  )
}

export const KPIsSection: React.FC<{plantCode: string}> = ({plantCode}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {startOfChart, endOfChart, startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const {scheduleId} = usePlanningChartScheduleData()
  const {data: schedule} = useScheduleQuery({
    plantId: plantCode,
    range: [startOfPlan, endOfPlan],
    scheduleId,
    useSideEffect: usePeriodicAutofillCheck
  })
  const {settings: kpiTableSettings, updateSettings: setKpiTableSettings} = useLocalStorageSettings(
    ProductionPlanningLSSettingsKeys.PlanningKPITableSettings,
    {isCardOpen: false}
  )
  const {isCardOpen} = kpiTableSettings

  const toggleIsKPITableMinimized = useCallback(
    () => setKpiTableSettings({isCardOpen: !isCardOpen}),
    [setKpiTableSettings, isCardOpen]
  )

  // create object containing both startOfChart and endOfChart so I can debounce both values as single object in lane below
  const fromTo = useMemo(() => ({startOfChart, endOfChart}), [startOfChart, endOfChart])
  const {startOfChart: debouncedStartOfChart, endOfChart: debouncedEndOfChart} = useDebouncedValue({
    value: fromTo,
    wait: 500
  }).debouncedValue

  return (
    <CollapsibleCard
      isCollapsed={!isCardOpen}
      onToggleCollapsed={toggleIsKPITableMinimized}
      title={t('planning.kpiTable.kpiCostAndProduction')}
      subtitle={
        schedule ? (
          <KPIsSectionSubtitle
            scheduleId={schedule.scheduleId}
            from={debouncedStartOfChart}
            to={debouncedEndOfChart}
          />
        ) : (
          <Skeleton variant="text" sx={{width: ({spacing}) => spacing(30)}} />
        )
      }
      {...dataTestId('kpi_card')}
    >
      {schedule ? (
        <KPISectionTable
          scheduleId={schedule.scheduleId}
          isExpanded={isCardOpen}
          from={debouncedStartOfChart}
          to={debouncedEndOfChart}
        />
      ) : (
        <Skeleton
          variant="rectangular"
          sx={{width: 1, height: ({spacing}) => spacing(isCardOpen ? 30 : 5)}}
        />
      )}
    </CollapsibleCard>
  )
}
